import './index.css'
import clsx from 'clsx'

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import type { Point, Rect } from 'types/screen'
import type { CSSProperties } from 'react'
import { ThemeContext } from 'contexts/theme'
import { getPageXOffset, getPageYOffset } from '@disco/utils/dom'
const VIEWPORT_MARGIN = 8

const getRectForNode = (node: HTMLElement): Rect => {
  const rect = node.getBoundingClientRect()
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  }
}

const getRectCenter = (rect: Rect): Point => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
})

const getViewport = () => ({
  width: document.body?.clientWidth ?? 0,
  height: window.innerHeight,
})

const getIntersectionWithViewport = (rect: Rect): Rect => {
  const viewport = getViewport()
  const top = Math.max(rect.top, 0)
  const left = Math.max(rect.left, 0)
  const bottom = Math.min(rect.top + rect.height, viewport.height)
  const right = Math.min(rect.left + rect.width, viewport.width)
  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
  }
}

const getVerticalPosition = (activatorRect: Rect, tooltipRect: Rect) => {
  const viewport = getViewport()
  const tooltipBottom =
    activatorRect.top +
    activatorRect.height +
    tooltipRect.height +
    VIEWPORT_MARGIN
  return tooltipBottom > viewport.height ? 'top' : 'bottom'
}

const getTooltipStyle = (
  activatorRect: Rect,
  tooltipRect: Rect,
  position: 'bottom' | 'top',
): { bottom?: number; left: number; width: number; top?: number } => {
  const viewport = getViewport()
  const scrollOffset = {
    x: getPageXOffset(),
    y: getPageYOffset(),
  }
  const maxOffsetLeft = viewport.width - tooltipRect.width - VIEWPORT_MARGIN
  const preferredOffsetLeft =
    activatorRect.left + activatorRect.width / 2 - tooltipRect.width / 2
  const offsetLeft =
    scrollOffset.x + Math.min(maxOffsetLeft, preferredOffsetLeft)
  const left = Math.round(Math.max(offsetLeft, VIEWPORT_MARGIN))
  const width = Math.min(
    tooltipRect.width,
    viewport.width - VIEWPORT_MARGIN * 2,
  )

  if (position === 'top') {
    return {
      bottom: Math.round(
        viewport.height - activatorRect.top - scrollOffset.y + VIEWPORT_MARGIN,
      ),
      left,
      width,
    }
  } else {
    return {
      top: Math.round(
        activatorRect.height +
          activatorRect.top +
          scrollOffset.y +
          VIEWPORT_MARGIN,
      ),
      left,
      width,
    }
  }
}

type Props = {
  children: React.ReactNode
  content: React.ReactNode | null | undefined
  maxWidth?: string
  marginTop?: string
  hidden?: boolean
  // Optional preferred position
  position?: 'bottom' | 'top'
  block?: boolean
}

/**
 * A tooltip component that displays additional information on hover
 * @param children - The element that triggers the tooltip on hover
 * @param content - The content to display in the tooltip
 * @param maxWidth - Optional maximum width for the tooltip
 * @param marginTop - Optional top margin for the tooltip
 * @param hidden - Whether the tooltip is hidden
 * @param position - Preferred position for the tooltip ('bottom' or 'top')
 * @param block - Whether the activator should be a block element
 * @returns A tooltip component with hover functionality
 */
const Tooltip = (props: Props): React.ReactNode | React.ReactElement<'div'> => {
  const containerRef = useRef<HTMLElement | null>(null)
  const activatorRef = useRef<HTMLDivElement | null>(null)
  const tooltipRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    containerRef.current =
      document.getElementById('app') || document.getElementById('__next')
  }, [])
  const [isActive, setIsActive] = useState(false)
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties | undefined>()
  const [tooltipPointerStyle, setTooltipPointerStyle] =
    useState<CSSProperties | undefined>()
  const [tooltipPosition, setTooltipPosition] = useState(props.position)
  const theme = useContext(ThemeContext).theme
  const setTooltipRef = useCallback(
    (node: HTMLElement | null) => {
      const activatorRect =
        activatorRef.current && getRectForNode(activatorRef.current)

      if (tooltipRef.current) {
        setTooltipStyle(undefined)
        setTooltipPointerStyle(undefined)
      }

      if (node && activatorRect) {
        const tooltipRect = getRectForNode(node)
        const verticalPosition =
          props.position || getVerticalPosition(activatorRect, tooltipRect)
        setTooltipPosition(verticalPosition)
        const tooltipStyle = getTooltipStyle(
          activatorRect,
          tooltipRect,
          verticalPosition,
        )
        setTooltipStyle(tooltipStyle)

        const visibleActivatorRect = getIntersectionWithViewport(activatorRect)
        const pointerOffsetLeft =
          getPageXOffset() +
          getRectCenter(visibleActivatorRect).x -
          tooltipStyle.left
        setTooltipPointerStyle({
          display:
            pointerOffsetLeft > tooltipStyle.width - VIEWPORT_MARGIN
              ? 'none'
              : undefined,
          left: pointerOffsetLeft,
        })
      }

      tooltipRef.current = node
    },
    [props.position],
  )

  const handleMouseLeave = () => {
    setIsActive(false)
  }

  const handleMouseOver = () => {
    setIsActive(true)
  }

  const renderPortal = (container: HTMLElement) => {
    return ReactDOM.createPortal(
      <div
        className={clsx('tooltip', {
          'tooltip--dark-theme': theme === 'dark',
          'tooltip--light-theme': theme === 'light',
        })}
        ref={setTooltipRef}
        style={{
          ...tooltipStyle,
          maxWidth: props.maxWidth || 'auto',
          marginTop: props.marginTop || 'auto',
        }}
        onMouseOver={handleMouseOver}
      >
        <div
          className={`tooltip-pointer tooltip-pointer--${
            tooltipPosition ?? 'bottom'
          }`}
          style={tooltipPointerStyle}
        />
        {props.content}
      </div>,
      container,
    )
  }

  if (!props.content) {
    return props.children
  }

  return (
    <div
      ref={activatorRef}
      className={clsx('tooltip__activator', {
        'tooltip__activator--block': props.block,
      })}
      onMouseLeave={handleMouseLeave}
      onMouseOver={handleMouseOver}
    >
      {props.children}
      {isActive &&
        !props.hidden &&
        containerRef.current &&
        renderPortal(containerRef.current)}
    </div>
  )
}

export default Tooltip
